import { atom, useRecoilState } from "recoil";
import { recoilPersist } from "recoil-persist";
import { useDAppCommand } from "../../libs/dweb/DAppCommand";
import React from "react";
import { TObject } from "corexxx";
import { DWebTS } from "../../libs/common_ts/DWebTS";

const ENDPOINT = "/api/nodel_remote_db_secure_grodok_money_networth";

// Type
export type TInvestingType = 'FIXED_DEPOSIT' | "ISA" | "REAL_ESTATE" | "SAVING_ACCOUNT" | "TRADING_ACCOUNT" | "RETIREMENT" | "MORTGAGE"
export type TInvestingState = "ACTIVE" | 'MATURED' | 'REPLACTED'
export type TInvestingCurrency = 'INR' | 'GBP' | 'USD'
export type TAccountEntry = {
    acccount_id: string,
    investment_type: TInvestingType
    banker_name: string,
    priciple_account: number;
    maturity_amount: number;
    currency: TInvestingCurrency,
    // FD
    start_date: string,
    maturity_date: string,
    interest_rate: number,
    comments: string,
    state: TInvestingState,
    // backend
    _id?: string
    ts_insert?: string,

}

export type TAccountSummary = {
    all_data: TAccountEntry[],
    distribution2: TObject, // by asserts
    distribution1: TObject,
    distribution3: TObject, // by finance instutution
    contributed_data: TAccountEntry[]
}

// helper
function buildEntry(entry: TAccountEntry[], target_currency: TInvestingCurrency): TAccountSummary {

    let entryCopy = JSON.parse(JSON.stringify(entry)) as TAccountEntry[];
    let distribution1 = {
        total_asserts: 0,
        total_liabilities: 0
    }
    let distribution2 = {
        FIXED_DEPOSIT: 0,
        ISA: 0,
        REAL_ESTATE: 0,
        SAVING_ACCOUNT: 0,
        TRADING_ACCOUNT: 0,
        RETIREMENT: 0
    }
    let contributed_data: TAccountEntry[] = []

    entryCopy.forEach(x => {
        if (x.state != 'ACTIVE') {
            return;
        }
        // convertion
        x.priciple_account = DWebTS.currencyConversion(x.priciple_account, x.currency, target_currency);
        // distribution1
        if (x.investment_type == 'MORTGAGE') {
            distribution1.total_liabilities += x.priciple_account;
        } else {
            distribution1.total_asserts += x.priciple_account;
            distribution2[x.investment_type] += x.priciple_account
        }
        contributed_data.push(x)
    })

    let distribution3: TObject = {}
    contributed_data.forEach(x => {
        if (!distribution3[x.banker_name]) {
            distribution3[x.banker_name] = { name: x.banker_name, investment: {} }
        }
        if (!distribution3[x.banker_name].investment[x.investment_type]) {
            distribution3[x.banker_name].investment[x.investment_type] = 0
        }
        distribution3[x.banker_name].investment[x.investment_type] += x.priciple_account;
    })

    return {
        all_data: entryCopy,
        contributed_data: DWebTS.sortByInsertTs(contributed_data),
        distribution1: distribution1,
        distribution2: distribution2,
        distribution3: distribution3,
    }
}

// Hook
const { persistAtom } = recoilPersist();
const accountSummaryAtom = atom<TAccountSummary>({
    key: "accountSummaryAtom",
    default: {
        all_data: [],
        contributed_data: [],
        distribution1: {},
        distribution2: {},
        distribution3: {}
    },
    effects_UNSTABLE: [persistAtom],
});

const rawEntryAtom = atom<TAccountEntry[]>({
    key: "rawEntryAtom",
    default: [],
    effects_UNSTABLE: [persistAtom],
});

export const useMoney = () => {
    let appCommand = useDAppCommand();
    let [rawEntry, setRawEntry] = useRecoilState(rawEntryAtom);
    let [accountSummary, setAccountSummary] = useRecoilState(accountSummaryAtom);

    React.useEffect(() => {
        setAccountSummary(buildEntry(rawEntry, appCommand?.accountState?.currency || 'GBP'))
    }, [rawEntry])

    const onInit_ = async () => {
        await fetchAndBuildSummary_();
    }

    const fetchAndBuildSummary_ = async () => {
        appCommand.fireAndForget(async () => {
            let res = await appCommand.api.postSimpleStore(ENDPOINT, { "_limit": 100 })
            let data = res.out as TAccountEntry[]
            setRawEntry(data)
        }, {
            success_msg: "Account information loaded",
            error_msg: "Not able to information",
        })
    }
    const _replaced = async (x: TAccountEntry) => {
        await appCommand.api.postSimpleStore(ENDPOINT + '/update', { _id: x._id!!, state: 'REPLACED' })
    }

    const _addAccountEntry = async (entry: TAccountEntry) => {
        await appCommand.api.safeCall(async () => {
            // Invlidate any older entry
            let f = await appCommand.api.postSimpleStore(ENDPOINT + '/find', { acccount_id: entry.acccount_id, state: 'ACTIVE' })
            for (let x of f.out) {
                let g = await appCommand.api.postSimpleStore(ENDPOINT + '/update', { _id: x._id, state: 'REPLACED' })
            }
            await appCommand.api.postSimpleStore(ENDPOINT + '/insert', entry)
        })
        fetchAndBuildSummary_();
    }

    const _addPreviewList = (list: TAccountEntry[]) => {
        setRawEntry([...rawEntry, ...list])
    }

    const _verifyAndFix = () => {
        let sortedData = DWebTS.sortByInsertTs<TAccountEntry>(accountSummary.contributed_data);
        sortedData.reverse()
        // new date to old date. 
        let st = new Set()
        let flag = 0;
        sortedData.forEach(x => {
            if (st.has(x.acccount_id)) {
                flag = 1
                appCommand.setNotification({ type: 'error', msg: "Duplicate entry found for " + x.acccount_id + '. Making it replaced' })
                _replaced(x);
            } else {
                st.add(x.acccount_id)
            }
        })
        if (flag == 0) {
            appCommand.setNotification({ type: 'success', msg: "No issues found in contributed data" })
        }
    }

    return {
        state: {
            accountSummary: accountSummary,
            rawEntry: rawEntry,
        },
        api: {
            refreshAccountSummary: fetchAndBuildSummary_,
            onInit: onInit_,
            addAccountEntry: _addAccountEntry,
            addPreviewList: _addPreviewList,
            verifyAndFix: _verifyAndFix,
        }
    }
}